import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '@/api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface LockerReservation {
  reservation_id: number
  locker_id: number
  locker_number: number
  member_id: number
  member_name: string | null
  phone: string | null
  start_date: string
  end_date: string
  status: number
  created_at: string
}

interface LockerReservationDetail {
  reservation_id: number
  locker_id: number
  locker_number: number
  member_id: number
  member_name: string | null
  login_id: string | null
  phone: string | null
  start_date: string
  end_date: string
  status: number
  emptied_at: string | null
  notified_at: string | null
  created_at: string
  updated_at: string
  consent_agreed: number
  consent_agreed_at: string | null
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: '0', label: '확인대기' },
  { value: '1', label: '확인완료' },
  { value: '2', label: '신청취소' },
  { value: '3', label: '이용종료' },
]

const STATUS_STYLE: Record<number, { label: string; cls: string }> = {
  0: { label: '확인대기', cls: 'bg-[#fff8e1] text-[#e67e22]' },
  1: { label: '확인완료', cls: 'bg-[#e8f5e9] text-[#388e3c]' },
  2: { label: '신청취소', cls: 'bg-[#f5f5f5] text-[#9e9e9e]' },
  3: { label: '이용종료', cls: 'bg-[#e3f2fd] text-[#1976d2]' },
}

const LIMIT = 20

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminLockerReservationTab() {
  const navigate = useNavigate()
  const [reservations, setReservations] = useState<LockerReservation[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [skip, setSkip] = useState(0)
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null)
  const [excelLoading, setExcelLoading] = useState(false)

  // 상세
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<LockerReservationDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const fetchReservations = useCallback(async (
    status: string, from: string, to: string, skipVal: number
  ) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (status !== '') params.append('status', status)
      if (from !== '') params.append('from_start_date', from)
      if (to !== '') params.append('to_start_date', to)
      params.append('skip', String(skipVal))
      params.append('limit', String(LIMIT))
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/admin/locker/reservations?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setReservations(data.reservations)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchReservations(statusFilter, fromDate, toDate, skip)
  }, [statusFilter, fromDate, toDate, skip, fetchReservations])

  const fetchDetail = async (id: number) => {
    setDetailLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/admin/locker/reservations/${id}`))
      if (!res.ok) throw new Error('조회 실패')
      setDetail(await res.json())
    } catch {
      alert('상세 정보를 불러오는 데 실패했습니다.')
    } finally {
      setDetailLoading(false)
    }
  }

  const handleRowClick = (id: number) => {
    setSelectedId(id)
    setDetail(null)
    fetchDetail(id)
  }

  const handleConfirm = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('확인완료 처리하시겠습니까?')) return
    setActionLoadingId(id)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/admin/locker/reservations/${id}/confirm`),
        { method: 'PATCH' }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchReservations(statusFilter, fromDate, toDate, skip)
      if (selectedId === id) fetchDetail(id)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleCancel = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('취소 처리하시겠습니까?')) return
    setActionLoadingId(id)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/admin/locker/reservations/${id}/cancel`),
        { method: 'PATCH' }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchReservations(statusFilter, fromDate, toDate, skip)
      if (selectedId === id) fetchDetail(id)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleExcelDownload = async () => {
    setExcelLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter !== '') params.append('status', statusFilter)
      if (fromDate !== '') params.append('from_start_date', fromDate)
      if (toDate !== '') params.append('to_start_date', toDate)
      const token = localStorage.getItem('access_token')
      const res = await fetch(getApiUrl(`/marubit/api/admin/locker/reservations/excel?${params.toString()}`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('다운로드 실패')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `locker_reservations_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('엑셀 다운로드에 실패했습니다.')
    } finally {
      setExcelLoading(false)
    }
  }

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })

  const formatDateTime = (d: string) =>
    new Date(d).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    })

  const totalPages = Math.ceil(total / LIMIT)
  const currentPage = Math.floor(skip / LIMIT) + 1

  return (
    <div className="flex gap-4">
      {/* 목록 */}
      <div className={`flex-1 min-w-0 transition-all ${selectedId ? 'max-w-[calc(100%-340px)]' : ''}`}>
        {/* Filter */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-wrap gap-3 items-end justify-between">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">예약 상태</label>
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setSkip(0) }}
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[130px] bg-white"
              >
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">이용 시작일 (from)</label>
              <input
                type="date" value={fromDate}
                onChange={e => { setFromDate(e.target.value); setSkip(0) }}
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] bg-white"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">이용 시작일 (to)</label>
              <input
                type="date" value={toDate}
                onChange={e => { setToDate(e.target.value); setSkip(0) }}
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] bg-white"
              />
            </div>
            {(fromDate || toDate) && (
              <button
                onClick={() => { setFromDate(''); setToDate(''); setSkip(0) }}
                className="text-[12px] text-gray-400 hover:text-gray-600 border border-[#e8e0dc] rounded-lg px-3 py-2 bg-white cursor-pointer transition-colors h-[38px]"
              >초기화</button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[13px] text-gray-400">
              총 <span className="text-[#887267] font-bold">{total}</span>건
            </span>
            <button
              onClick={handleExcelDownload}
              disabled={excelLoading}
              className="bg-[#388e3c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg cursor-pointer hover:bg-[#2e7d32] transition-colors disabled:opacity-50 border-none"
            >
              {excelLoading ? '다운로드 중...' : '📥 엑셀로 저장'}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          {loading ? (
            <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">불러오는 중...</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[70px]">금고번호</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">이름</th>
                  <th className="hidden sm:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[130px]">전화번호</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">이용 시작일</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">이용 종료일</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">상태</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]"></th>
                </tr>
              </thead>
              <tbody>
                {reservations.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-300 text-[13px]">예약 내역이 없습니다.</td>
                  </tr>
                ) : reservations.map(r => {
                  const st = STATUS_STYLE[r.status] ?? { label: String(r.status), cls: 'bg-gray-100 text-gray-500' }
                  const isLoading = actionLoadingId === r.reservation_id
                  return (
                    <tr
                      key={r.reservation_id}
                      className={`border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer ${
                        selectedId === r.reservation_id ? 'bg-[#fdf8f0]' : ''
                      }`}
                      onClick={() => handleRowClick(r.reservation_id)}
                    >
                      <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a] whitespace-nowrap">
                        {r.locker_number}번
                      </td>
                      <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{r.member_name ?? '-'}</td>
                      <td className="hidden sm:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">{r.phone ?? '-'}</td>
                      <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{formatDate(r.start_date)}</td>
                      <td className="hidden md:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">{formatDate(r.end_date)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        {r.status === 0 && (
                          <div className="flex gap-1.5">
                            <button
                              onClick={e => handleConfirm(e, r.reservation_id)}
                              disabled={isLoading}
                              className="text-[12px] text-[#388e3c] hover:text-[#2e7d32] border border-[#c8e6c9] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50"
                            >확인</button>
                            <button
                              onClick={e => handleCancel(e, r.reservation_id)}
                              disabled={isLoading}
                              className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50"
                            >취소</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1.5 mt-5">
            <button onClick={() => setSkip((currentPage - 2) * LIMIT)} disabled={currentPage === 1}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer">‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1)
                  acc.push('...')
                acc.push(p)
                return acc
              }, [])
              .map((p, idx) =>
                p === '...'
                  ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-gray-300">…</span>
                  : <button key={p} onClick={() => setSkip(((p as number) - 1) * LIMIT)}
                      className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${currentPage === p ? 'bg-[#887267] text-white border-[#887267]' : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'}`}>{p}</button>
              )}
            <button onClick={() => setSkip(currentPage * LIMIT)} disabled={currentPage === totalPages}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer">›</button>
          </div>
        )}
      </div>

      {/* 상세 패널 */}
      {selectedId && (
        <div className="w-[320px] flex-shrink-0">
          <div className="bg-white rounded-2xl border border-[#e8e0dc] shadow-[0_2px_12px_rgba(136,114,103,0.06)] sticky top-8">
            {/* 패널 헤더 */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#f5f2f0]">
              <span className="text-[13px] font-bold text-[#2a1f1a]">예약 상세</span>
              <button
                onClick={() => { setSelectedId(null); setDetail(null) }}
                className="text-[16px] text-gray-400 hover:text-gray-600 cursor-pointer bg-transparent border-none leading-none"
              >✕</button>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-300 text-[13px]">불러오는 중...</div>
            ) : detail ? (
              <div className="px-5 py-4 flex flex-col gap-4">
                {/* 상태 배지 */}
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-gray-400">예약 #{detail.reservation_id}</span>
                  {(() => {
                    const st = STATUS_STYLE[detail.status] ?? { label: String(detail.status), cls: 'bg-gray-100 text-gray-500' }
                    return <span className={`text-[11px] px-2.5 py-1 rounded-lg font-semibold ${st.cls}`}>{st.label}</span>
                  })()}
                </div>

                <div className="border-t border-[#f5f2f0]" />

                {/* 금고 정보 */}
                <div>
                  <p className="text-[11px] text-[#5a3d32] font-bold mb-2">금고 정보</p>
                  <div className="flex flex-col gap-2">
                    <InfoRow label="금고번호" value={`${detail.locker_number}번`} />
                    <InfoRow label="이용 시작일" value={formatDate(detail.start_date)} />
                    <InfoRow label="이용 종료일" value={formatDate(detail.end_date)} />
                    {detail.emptied_at && (
                      <InfoRow label="비움 처리일" value={formatDateTime(detail.emptied_at)} />
                    )}
                    <InfoRow label="동의 여부" value={detail.consent_agreed === 1 ? '동의' : '미동의'} />
                  </div>
                </div>

                <div className="border-t border-[#f5f2f0]" />

                {/* 신청자 정보 */}
                <div>
                  <p className="text-[11px] text-[#5a3d32] font-bold mb-2">신청자 정보</p>
                  <div className="flex flex-col gap-2">
                    <InfoRow label="아이디" value={detail.login_id ?? '-'} />
                    <InfoRow label="이름" value={detail.member_name ?? '-'} />
                    <InfoRow label="전화번호" value={detail.phone ?? '-'} />
                    <InfoRow label="신청일" value={formatDateTime(detail.created_at)} />
                  </div>
                </div>

                {/* Actions */}
                {detail.status === 0 && (
                  <>
                    <div className="border-t border-[#f5f2f0]" />
                    <div className="flex gap-2">
                      <button
                        onClick={e => handleCancel(e, detail.reservation_id)}
                        disabled={actionLoadingId === detail.reservation_id}
                        className="flex-1 text-[12px] text-red-400 font-semibold py-2 rounded-lg border border-red-100 bg-white cursor-pointer hover:bg-red-50 transition-colors disabled:opacity-50"
                      >취소 처리</button>
                      <button
                        onClick={e => handleConfirm(e, detail.reservation_id)}
                        disabled={actionLoadingId === detail.reservation_id}
                        className="flex-1 bg-[#887267] text-white text-[12px] font-semibold py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors disabled:opacity-50"
                      >확인 처리</button>
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-[11px] text-gray-400 shrink-0">{label}</span>
      <span className="text-[12px] text-[#2a1f1a] text-right">{value}</span>
    </div>
  )
}